/**
 * @fileoverview GBP group text input for when there are too many GBP groups in a given account to use a dropdown, which would otherwise cause a timeout.
 *
 * @author Stephanie Yoon (steyoon@yext.com)
 */
import {useDebounce} from '/src/com/yext/listingslaunchstorm/js/common/hooks/usedebounce';
import {LinkedAccount} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/linkedaccount';
import {useValidateGbpGroup} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/datasource/usevalidategbpgroup';
import {Button} from '/ui/components/button/button';
import {Checkbox} from '/ui/components/checkbox/checkbox';
import {Input} from '/ui/components/input/input';
import {Table} from '/ui/components/table/table';
import {Tooltip} from '/ui/components/tooltip/tooltip';

import * as styles from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/gbp/gbp.module.scss';
import CheckedSvg from '/src/com/yext/listingslaunchstorm/public/icons/green-checkmark.svg';

/**
 * @param {{
 *  linkedAccount: LinkedAccount,
 *  groupId: (string | undefined),
 *  fetchedGroupIds: !Array<string>,
 *  setGroupConfiguration: function(string=): void,
 *  disabled: boolean,
 * }} props
 */
export function GbpGroupInput({linkedAccount, fetchedGroupIds, setGroupConfiguration, groupId, disabled}) {
  const [groupIdInput, setGroupIdInput] = React.useState(groupId ?? '');
  const [inProgress, setInProgress] = React.useState(false);
  const {status, data, refetch, abort} = useValidateGbpGroup();

  const validate = React.useCallback((linkedAccountId, newGroupId) => {
    if (newGroupId.length > 0) {
      refetch(linkedAccountId, newGroupId);
      setInProgress(false);
    }
  }, [refetch]);

  const {debouncedCallback, dispose} = useDebounce(validate, 1000);

  const isFetched = fetchedGroupIds.includes(groupIdInput);
  const isError = !isFetched && status === 'error';
  const isLoading = !isFetched && (groupIdInput.length === 0 || inProgress || status === 'loading');
  const isValid = (data !== undefined && data.valid) || isFetched;
  const isInvalid = data !== undefined && !data.valid && !isFetched;

  React.useEffect(() => {
    setInProgress(!isFetched);
    dispose();
    abort();
  }, [linkedAccount, dispose, abort, isFetched]);

  React.useEffect(() => {
    if (inProgress) {
      debouncedCallback(linkedAccount.id, groupIdInput);
    } else if (isValid) {
      setGroupConfiguration(groupIdInput);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress, isValid, groupIdInput]);

  return (
    <div className={styles.gbpGroupInput}>
      <Table
        columns={getColumns()}
        dataRows={[
          {
            id: 'groupIdMenu',
            [COLUMN_IDS.GROUP_ID]:
              <ul>
                <li>
                  <GbpGroupValidationMessage
                    groupIdInput={groupIdInput}
                    linkedAccountId={linkedAccount.id}
                    isLoading={isLoading}
                    isError={isError}
                    isValid={isValid}
                    isInvalid={isInvalid}
                    validate={validate}
                  />
                </li>
                <li>
                  <div className={styles.gbpGroupRow}>
                    <div className={styles.gbpGroupText}>
                      <Input
                        label={yext.msg('Google Location Group ID')}
                        disabled={disabled}
                        hideLabel={true}
                        type="text"
                        valid={isLoading || (!isError && !isInvalid)}
                        value={groupIdInput}
                        onChange={newGroupId => {
                          setGroupIdInput(newGroupId);
                          setInProgress(!fetchedGroupIds.includes(newGroupId));
                        }}
                        placeholder={yext.msg('Enter Location Group ID')}
                      />
                    </div>
                  </div>
                </li>
              </ul>,
          },
        ]}
        label={yext.msg('Location Group ID Input')}
      />
    </div>
  );
}

const GbpGroupValidationMessage = React.memo(
  function GbpGroupValidationMessage({groupIdInput, linkedAccountId, isLoading, isError, isValid, isInvalid, validate}) {
    if (isLoading) {
      return <div></div>;
    }
    if (isError) {
      return <div className={styles.gbpGroupError}>
        <>
          {yext.msg('There was an error validating this Location Group ID.')}&nbsp;
          <Button chromeless={true} onClick={() => validate(linkedAccountId, groupIdInput)}>{yext.msg('Please click to retry.')}</Button>
        </>
      </div>;
    }
    if (isValid) {
      return <div className={styles.gbpGroupValid}>
        <CheckedSvg />{yext.msg('Valid!')}
      </div>;
    }
    if (isInvalid) {
      return <div className={styles.gbpGroupError}>
        {yext.msg('Invalid. ID may be incorrect or unauthorized.')}
      </div>;
    }
    return <div></div>;
  });


const COLUMN_IDS = {
  GROUP_ID: 'groupId',
};

function getColumns() {
  return [
    {
      id: COLUMN_IDS.GROUP_ID,
      label: <Tooltip
        unclearText={yext.msg('Google Location Group ID')}
        tooltipText={yext.msg('Enter your Google Location Group ID to manage your location group. You can find your Location Group ID in your Google Business Profile account.')}
      />,
    },
  ];
}
